import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { tokenStorage } from "./tokenStorage";
import { sessionStorage } from "../storage/sessionStorage";
import { getMobileToken } from "../storeAccessor";

const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

interface RefreshTokenResponse {
  status: boolean;
  alert: boolean;
  message: string;
  data: {
    token_type: string;
    access_token: string;
    refresh_token: string;
  };
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL!;

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

axiosInstance.interceptors.request.use(
  async (config) => {
        let token = tokenStorage.getAccessToken() ?? getMobileToken() ?? undefined;

    if (token && !isRefreshing) {
      const exp = getTokenExpiry(token);
      const nowSecs = Math.floor(Date.now() / 1000);

      if (exp !== null && exp - nowSecs < 60) {
        const refreshToken = tokenStorage.getRefreshToken();
        if (refreshToken) {
          isRefreshing = true;
          try {
            const response = await axios.post<RefreshTokenResponse>(
              `${API_BASE_URL}/auth/token/refresh`,
              { refresh_token: refreshToken },
            );
            const { access_token, refresh_token: newRefreshToken } =
              response.data.data;
            tokenStorage.setTokens(access_token, newRefreshToken);
            processQueue(null, access_token);
            token = access_token;
          } catch (err) {
            tokenStorage.clearTokens();
            processQueue(err, null);
          } finally {
            isRefreshing = false;
          }
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["X-Session-Id"] = sessionStorage.getSessionId();
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        tokenStorage.clearTokens();
        processQueue(error, null);
        isRefreshing = false;
        if (
          typeof window !== "undefined" &&
          !("ReactNativeWebView" in window) &&
          !window.location.pathname.startsWith("/auth/")
        ) {
          const redirect = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/auth/login?redirect=${redirect}`;
        }
        return Promise.reject(error);
      }

      try {
        const response = await axios.post<RefreshTokenResponse>(
          `${API_BASE_URL}/auth/token/refresh`,
          { refresh_token: refreshToken },
        );

        const { access_token, refresh_token: newRefreshToken } =
          response.data.data;

        tokenStorage.setTokens(access_token, newRefreshToken);
        processQueue(null, access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        tokenStorage.clearTokens();
        processQueue(refreshError, null);
        if (
          typeof window !== "undefined" &&
          !("ReactNativeWebView" in window) &&
          !window.location.pathname.startsWith("/auth/")
        ) {
          const redirect = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/auth/login?redirect=${redirect}`;
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
